import { createContext, useContext, useState, useEffect, useMemo } from 'react'

interface LanguageContextType {
  language: string
  setLanguage: (lang: string) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

// Minimal fallback translations if public files fail to load
const fallbackTranslations: Record<string, any> = {
  en: {
    common: { home: 'Home' },
  },
  dr: {
    common: { home: 'صفحه اصلی' },
  },
  ps: {
    common: { home: 'کور پاڼه' },
  },
}

function getFromPath(obj: any, path: string): any {
  return path.split('.').reduce((acc: any, part: string) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj)
}

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguage] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      try {
        return localStorage.getItem('lang') || 'en'
      } catch {
        return 'en'
      }
    }
    return 'en'
  })

  const [loadedTranslations, setLoadedTranslations] = useState<Record<string, any>>({})

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('lang', language)
      } catch {}
    }
  }, [language])

  // Load and merge translation files from public/translations/{lang}.json
  useEffect(() => {
    let aborted = false

    function deepMerge(target: any, source: any) {
      if (typeof target !== 'object' || target === null) return source
      const result: any = Array.isArray(target) ? [...target] : { ...target }
      for (const key of Object.keys(source || {})) {
        const tVal = (target || {})[key]
        const sVal = source[key]
        if (typeof sVal === 'object' && sVal !== null && !Array.isArray(sVal)) {
          result[key] = deepMerge(tVal || {}, sVal)
        } else {
          result[key] = sVal
        }
      }
      return result
    }

    async function fetchJson(lang: string) {
      const base = (import.meta as any)?.env?.BASE_URL || '/'
      const url = `${base.replace(/\/$/, '')}/translations/${lang}.json`
      const res = await fetch(url, { cache: 'no-cache' })
      if (!res.ok) throw new Error(`Failed to load translations for ${lang}`)
      return res.json()
    }

    async function load() {
      try {
        if (language === 'en') {
          const enData = await fetchJson('en')
          if (!aborted) setLoadedTranslations(enData)
          return
        }
        // Load English as base, then overlay selected language
        const [enData, langData] = await Promise.all([
          fetchJson('en'),
          fetchJson(language),
        ])
        const merged = deepMerge(enData, langData)
        if (!aborted) setLoadedTranslations(merged)
      } catch (e) {
        // Fallback to minimal built-in translations to avoid showing raw keys
        const fb = deepMerge(fallbackTranslations['en'] || {}, fallbackTranslations[language] || {})
        if (!aborted) setLoadedTranslations(fb)
      }
    }

    load()
    return () => {
      aborted = true
    }
  }, [language])

  const t = useMemo(() => {
    return (key: string): string => {
      const value = getFromPath(loadedTranslations, key)
      if (typeof value === 'string') return value
      const fb = getFromPath(fallbackTranslations[language] || {}, key)
      return typeof fb === 'string' ? fb : key
    }
  }, [loadedTranslations, language])

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
  }

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

// Helper function to get text from multilingual objects
export const getText = (multilingualObj: any): string => {
  if (!multilingualObj) return ''
  if (typeof multilingualObj === 'string') return multilingualObj
  const context = useContext(LanguageContext)
  if (context === undefined) {
    return multilingualObj.en || multilingualObj.per || multilingualObj.ps || ''
  }
  const { language } = context
  return multilingualObj[language] || multilingualObj.en || multilingualObj.per || multilingualObj.ps || ''
}
